import { useState, useMemo } from "react";
import moment from "moment";
import { switchString } from "../../utils/utils";
import { Button, Checkbox, Modal } from "src/components";
import InvoiceDropdown from "./invoice-menu.component";
import downArrow from "src/assets/down-arrow.svg";
import { useNavigate } from "react-router-dom";
import InvoiceReject from "../invoice-details/invoice-reject";
import InvoiceReassign from "../invoice-master/InvoiceReassign";
import Contact from "src/assets/Contact.svg";
import { downloadInvoice, acceptStockistAck } from "src/services/invoice-api";
import InvoiceEscalate from "../invoice-master/InvoiceEscalate";
import InvoiceSetPriority from "../invoice-master/InvoiceSetPriority";
import star from "src/features/fields/assets/star.svg";
import starEmpty from "src/assets/starEmpty.svg";
import InvoiceOnHold from "../invoice-master/InvoiceOnHold";
import Download from "src/features/fields/assets/Download copy.svg";
import Reject from "src/features/fields/assets/Reject.svg";
import Escalate from "src/features/fields/assets/Escalate.svg";
import Priority from "src/features/fields/assets/Priority.svg";
// import Hold from "src/features/fields/assets/Hold.svg"; //M10-10964
import { useToast } from "src/hooks/useToast";
import { useSelector } from "react-redux";
import { RootState } from "src/context/store";
import { useDispatch } from "react-redux";
import { invoiceActions } from "src/context/invoice-slice";
import { counterActions } from "src/context/counter-slice";
import { latestRemarks } from "../invoice-master/invoice-helper"; // M10-13315
import RejectionDetailsCard, { shouldShowRejectionDetails } from "./RejectionDetailsCard"; //M10-15065 //M10-16197

const menuItems = [
  { label: "Re-assign Invoice", id: "reassign", icon: Contact },
  // { label: "Put On hold", id: "hold", icon: Hold }, //M10-10964
  { label: "Reject Invoice", id: "reject", icon: Reject },
  { label: "Download Invoice", id: "download", icon: Download },
  { label: "Download E-Inv Ack", id: "download-ack", icon: Download },
  { label: "Download E-Way Bill", id: "download-eway", icon: Download },
  { label: "Escalate Invoice", id: "escalate", icon: Escalate },
  { label: "Set Priority", id: "setpriority", icon: Priority },
  { label: "Remove Priority", id: "removepriority", icon: Priority },
  { label: "Re-Entry", id: "re-entry", icon: Priority },  // M10-12366
  { label: "Accept Stockist Ack", id: "stockist-ack", icon: Priority },
];

interface InvoiceDetailCardProps {
  status: string;
  data: any;
  checkDataType: boolean;
  viewDetails?: boolean;
  isExpandable?: boolean;
  isExpandedInitial?: boolean;
  isEntry?: boolean;
  index?: number;
  invoiceData?: any;
  handleAddItem?: any;
  approvalScreen?: boolean;
  isEsalated?: boolean;
  fetchApprovalData?: () => Promise<void>;
  hasViewDetailsPermission: boolean;
  detailsPermissionLoading: boolean;
  hasPermissionReassign: boolean;
  reassignPermissionLoading: boolean;
  hasPermissionPutOnHold: boolean;
  putOnHoldPermissionLoading: boolean;
  hasPermissionReject: boolean;
  rejectPermissionLoading: boolean;
  hasPermissionEscalate: boolean;
  escalatePermissionLoading: boolean;
  hasPermissionSetPriority: boolean;
  hasPermissionReEntry: boolean;
  setPriorityPermissionLoading: boolean;
  reEntryPermissionLoading: boolean;
  hasPermissionStockistAck?: boolean;
  stockistAckPermissionLoading?: boolean;
  // REJECTION-REVIEW-WORKFLOW: when true, this card is rendered inside the
  // FUID Rejection tab. Disables all entry/edit actions; the parent tab
  // renders Accept/Reject buttons. View Details routes to a restricted view.
  isFuidRejectionReview?: boolean;
}

// M10-16200: category 1 e-invoice status badge colors
const cat1EinvoiceStatusColor: Record<string, string> = {
  pending: "text-[#92400E] bg-[#FEF3C7]",
  accepted: "text-[#207a65] bg-[#d3f6e4]",
  rejected: "text-[#B42318] bg-[#FEE4E2]",
};

const InvoiceApprovalDetailCard = ({
  data,
  checkDataType = true,
  status,
  viewDetails = true,
  isExpandable = true,
  isExpandedInitial = false,
  index,
  invoiceData,
  approvalScreen = false,
  hasViewDetailsPermission,
  detailsPermissionLoading,
  hasPermissionReassign,
  reassignPermissionLoading,
  hasPermissionPutOnHold,
  putOnHoldPermissionLoading,
  hasPermissionReject,
  rejectPermissionLoading,
  hasPermissionEscalate,
  escalatePermissionLoading,
  hasPermissionSetPriority,
  setPriorityPermissionLoading,
  hasPermissionReEntry,
  reEntryPermissionLoading,
  hasPermissionStockistAck = false,
  stockistAckPermissionLoading = false,
  isEsalated = true,
  fetchApprovalData,
  isFuidRejectionReview = false,
}: InvoiceDetailCardProps) => {
  const isRejectionPending = !!invoiceData?.is_rejection_pending;
  const [isExpanded, setIsExpanded] = useState(isExpandedInitial || index === 0);
  const naviagate = useNavigate();
  const [modalStates, setModalStates] = useState({
    reject: false,
    reassign: false,
    hold: false,
    escalate: false,
    setpriority: false,
    removepriority: false,
    reEntry: false,  // M10-12366
    stockistAck: false,
  });
  const [isAcceptingAck, setIsAcceptingAck] = useState(false);
  // Mandatory remark saved with the stockist acknowledgement.
  const [stockistAckRemarks, setStockistAckRemarks] = useState("");
  const { selectedInvoices } = useSelector((state: RootState) => state.invoice);
  const invoiceKey = invoiceData?.stockist_invoice_attachment_id;
  const isSelected = selectedInvoices.some((invoice: any) => invoice.key === invoiceKey && invoice.fuid_no === invoiceData?.fuid_no);
  const dispatch = useDispatch();
  const { InvoiceApprovalFilters } = useSelector((state: RootState) => state.invoice);
  const company_code = InvoiceApprovalFilters?.company?.[0]?.code;

  const { toast } = useToast();

  const isLoadingPermissions =
    reassignPermissionLoading || putOnHoldPermissionLoading || rejectPermissionLoading || escalatePermissionLoading || setPriorityPermissionLoading || reEntryPermissionLoading || stockistAckPermissionLoading;

  const permissionMap: Record<string, boolean> = {
    reassign: hasPermissionReassign,
    hold: hasPermissionPutOnHold,
    reject: hasPermissionReject,
    escalate: hasPermissionEscalate,
    setpriority: hasPermissionSetPriority,
    removepriority: hasPermissionSetPriority,
    "re-entry": hasPermissionReEntry,
    "stockist-ack": hasPermissionStockistAck,
  };

  // REJECTION-REVIEW-WORKFLOW: workflow-action ids that must be blocked while a
  // rejection review is pending OR when this card is rendered inside the FUID
  // Rejection tab. Downloads remain available.
  const blockedWhileRejectionPending = new Set([
    "reject",
    "reassign",
    "hold",
    "escalate",
    "setpriority",
    "removepriority",
    "re-entry",
    "stockist-ack",
  ]);

  const filteredMenuItems = useMemo(() => {
    if (isLoadingPermissions || !invoiceData) return [];

    return menuItems.filter((item) => {
      if (permissionMap.hasOwnProperty(item.id) && !permissionMap[item.id]) {
        return false;
      }
      if (!invoiceData?.is_eway_applicable && item.id === "download-eway") {
        return false;
      }
      if (invoiceData?.is_prioritised && item.id === "setpriority") {
        return false;
      }
      if (!invoiceData?.is_prioritised && item.id === "removepriority") {
        return false;
      }
      if ([2, 5, 8].includes(invoiceData?.fileStatusHdr?.fuid_status_id) && item.id === "reject") {
        return false;
      }
      if (invoiceData?.fileStatusHdr?.fuid_status_id === 8 && item.id === "reassign") {
        return false;
      }
      if (invoiceData?.fileStatusHdr?.fuid_status_id === 8 && item.id === "hold") {
        return false;
      }
      if (invoiceData?.fileStatusHdr?.fuid_status_id === 8 && item.id === "escalate") {
        return false;
      }
      if (invoiceData?.fileStatusHdr?.fuid_status_id !== 8 && item.id === "re-entry") {
        return false;
      }
      if (
        item.id === "stockist-ack" &&
        !(invoiceData?.fileStatusHdr?.fuid_status_id === 8 && invoiceData?.po_stockist_ack_status === "PENDING")
      ) {
        return false;
      }
      // REJECTION-REVIEW-WORKFLOW: lock down workflow actions while review pending
      if ((isRejectionPending || isFuidRejectionReview) && blockedWhileRejectionPending.has(item.id)) {
        return false;
      }
      return true;
    });
  }, [
    invoiceData,
    isLoadingPermissions,
    hasPermissionReassign,
    hasPermissionPutOnHold,
    hasPermissionReject,
    hasPermissionEscalate,
    hasPermissionSetPriority,
    hasPermissionReEntry,
    hasPermissionStockistAck,
    isRejectionPending,
    isFuidRejectionReview,
  ]);

  const verifyStringType = function (input: string) {
    const date = new Date(input);
    const numberRegex = /^\d+$/;
    if (!isNaN(date.getTime()) && input.trim() !== "" && !numberRegex.test(input)) {
      return moment(input).format("DD MMM, YYYY");
    } else {
      return input;
    }
  };

  const handleToggle = () => {
    setIsExpanded((prev) => !prev);
  };

  const handleCloseModal = (modalName: string) => {
    setModalStates((prev: any) => ({ ...prev, [modalName]: false }));
  };

  const handleConfirmStockistAck = async () => {
    const remarks = stockistAckRemarks.trim();
    if (!remarks) {
      toast({ description: "Remarks are required", variant: "destructive" });
      return;
    }
    setIsAcceptingAck(true);
    try {
      const res: any = await acceptStockistAck(invoiceData?.stockist_invoice_attachment_id, company_code, remarks);
      toast({ description: res?.data?.message || "Stockist acknowledgement accepted successfully", variant: "default" });
      handleCloseModal("stockistAck");
      fetchApprovalData?.();
    } catch (error: any) {
      console.log(error);
      toast({
        description: error?.response?.data?.message || error?.message || "Failed to accept the stockist acknowledgement",
        variant: "destructive",
      });
    } finally {
      setIsAcceptingAck(false);
    }
  };

  const handleOptionClick = (item: any) => {
    switch (item.id) {
      case "reject":
        setModalStates((prev: any) => ({ ...prev, reject: true }));
        break;
      case "hold":
        setModalStates((prev: any) => ({ ...prev, hold: true }));
        break;
      case "reassign":
        setModalStates((prev: any) => ({ ...prev, reassign: true }));
        break;
      // M10-12366
      case "re-entry":
        naviagate(`/transactions/invoice/${invoiceData.stockist_invoice_attachment_id}/2`, { state: { CompanyCode: company_code } });
        break;
      case "stockist-ack":
        setStockistAckRemarks("");
        setModalStates((prev: any) => ({ ...prev, stockistAck: true }));
        break;
      // M10-12366
      case "download":
        if (!invoiceData?.file_path) {
          toast({ description: "Invoice not found", variant: "destructive" });
          return;
        }
        downloadInvoice(invoiceData?.file_path, company_code).then((res: any) => {
          if (res?.data?.success && res?.data?.data) {
            window.open(res?.data?.data, "_blank"); // Opens the link in a new tab
          } else {
            console.error("Failed to download the invoice.");
          }
        });
        break;
      case "download-ack":
        if (!invoiceData?.einv_attachment_path) {
          toast({ description: "E-Invoice not found", variant: "destructive" });
          return;
        }
        downloadInvoice(invoiceData?.einv_attachment_path, company_code).then((res: any) => {
          if (res?.data?.success && res?.data?.data) {
            window.open(res?.data?.data, "_blank"); // Opens the link in a new tab
          } else {
            console.error("Failed to download the E-Invoice.");
          }
        });
        break;
      case "download-eway":
        if (invoiceData?.eway_attachment_path == null || invoiceData?.eway_attachment_path == "") {
          toast({ description: "E-Way Bill not found", variant: "destructive" });
          return;
        }
        downloadInvoice(invoiceData?.eway_attachment_path, company_code).then((res: any) => {
          if (res?.data?.success && res?.data?.data) {
            window.open(res?.data?.data, "_blank"); // Opens the link in a new tab
          } else {
            console.error("Failed to download the E-Way Bill.");
          }
        });
        break;
      default:
        break;
    }
  };

  return (
    <div className="mb-4">
      <div className="hidden">
        <Modal isOpen={modalStates.reject} isHeader={false} onCloseModal={() => handleCloseModal("reject")} title="Reject Invoice">
          <InvoiceReject
            // company_code={InvoiceApprovalFilters?.company}
            // division_code={InvoiceApprovalFilters?.division}
            // startDate={InvoiceApprovalFilters?.dateRange?.from}
            // endDate={InvoiceApprovalFilters?.dateRange?.to}
            // assignee_id={InvoiceApprovalFilters?.assignee}
            // city={InvoiceApprovalFilters?.city}
            // district={InvoiceApprovalFilters?.district}
            // state={InvoiceApprovalFilters?.state}
            // ordering={InvoiceApprovalFilters?.sorting}
            // status={InvoiceApprovalFilters?.status}
            tab={"approval"}
            invoiceData={invoiceData}
            onCloseModal={() => handleCloseModal("reject")}
            onSuccess={() => fetchApprovalData?.()}
          />
        </Modal>
        <Modal isOpen={modalStates.reassign} isHeader={false} onCloseModal={() => handleCloseModal("reassign")} title="Reassign Invoice">
          <InvoiceReassign
            // company_code={InvoiceApprovalFilters?.company}
            // division_code={InvoiceApprovalFilters?.division}
            // startDate={InvoiceApprovalFilters?.dateRange?.from}
            // endDate={InvoiceApprovalFilters?.dateRange?.to}
            // assignee_id={InvoiceApprovalFilters?.assignee}
            // city={InvoiceApprovalFilters?.city}
            // district={InvoiceApprovalFilters?.district}
            // state={InvoiceApprovalFilters?.state}
            // ordering={InvoiceApprovalFilters?.sorting}
            // status={InvoiceApprovalFilters?.status}
            tab={"approval"}
            invoiceData={invoiceData}
            onCloseModal={() => {
              handleCloseModal("reassign");
              fetchApprovalData?.();
            }}
          />
        </Modal>
        <Modal isOpen={modalStates.escalate} isHeader={false} onCloseModal={() => handleCloseModal("escalate")} title="Escalate Invoice">
          <InvoiceEscalate
            company_code={InvoiceApprovalFilters?.company}
            division_code={InvoiceApprovalFilters?.division}
            startDate={InvoiceApprovalFilters?.dateRange?.from}
            endDate={InvoiceApprovalFilters?.dateRange?.to}
            assignee_id={InvoiceApprovalFilters?.assignee}
            city={InvoiceApprovalFilters?.city}
            district={InvoiceApprovalFilters?.district}
            state={InvoiceApprovalFilters?.state}
            ordering={InvoiceApprovalFilters?.sorting}
            status={InvoiceApprovalFilters?.status}
            tab={"approval"}
            invoiceData={invoiceData}
            onCloseModal={() => {
              handleCloseModal("escalate");
              fetchApprovalData?.();
            }}
          />
        </Modal>
        <Modal isOpen={modalStates.setpriority} isHeader={false} onCloseModal={() => handleCloseModal("setpriority")} title="Set Priority">
          <InvoiceSetPriority
            company_code={InvoiceApprovalFilters?.company}
            division_code={InvoiceApprovalFilters?.division}
            startDate={InvoiceApprovalFilters?.dateRange?.from}
            endDate={InvoiceApprovalFilters?.dateRange?.to}
            assignee_id={InvoiceApprovalFilters?.assignee}
            city={InvoiceApprovalFilters?.city}
            district={InvoiceApprovalFilters?.district}
            state={InvoiceApprovalFilters?.state}
            ordering={InvoiceApprovalFilters?.sorting}
            status={InvoiceApprovalFilters?.status}
            tab={"approval"}
            unset={false}
            invoiceData={invoiceData}
            onCloseModal={() => {
              handleCloseModal("setpriority");
              fetchApprovalData?.();
            }}
          />
        </Modal>
        <Modal isOpen={modalStates.removepriority} isHeader={false} onCloseModal={() => handleCloseModal("removepriority")} title="Remove Priority">
          <InvoiceSetPriority
            company_code={InvoiceApprovalFilters?.company}
            division_code={InvoiceApprovalFilters?.division}
            startDate={InvoiceApprovalFilters?.dateRange?.from}
            endDate={InvoiceApprovalFilters?.dateRange?.to}
            assignee_id={InvoiceApprovalFilters?.assignee}
            city={InvoiceApprovalFilters?.city}
            district={InvoiceApprovalFilters?.district}
            state={InvoiceApprovalFilters?.state}
            ordering={InvoiceApprovalFilters?.sorting}
            status={InvoiceApprovalFilters?.status}
            tab={"approval"}
            unset={true}
            invoiceData={invoiceData}
            onCloseModal={() => {
              handleCloseModal("removepriority");
              fetchApprovalData?.();
            }}
          />
        </Modal>
        <Modal isOpen={modalStates.hold} isHeader={false} onCloseModal={() => handleCloseModal("hold")} title="Put on hold">
          <InvoiceOnHold
            company_code={InvoiceApprovalFilters?.company}
            division_code={InvoiceApprovalFilters?.division}
            startDate={InvoiceApprovalFilters?.dateRange?.from}
            endDate={InvoiceApprovalFilters?.dateRange?.to}
            assignee_id={InvoiceApprovalFilters?.assignee}
            city={InvoiceApprovalFilters?.city}
            district={InvoiceApprovalFilters?.district}
            state={InvoiceApprovalFilters?.state}
            ordering={InvoiceApprovalFilters?.sorting}
            status={InvoiceApprovalFilters?.status}
            tab={"approval"}
            invoiceData={invoiceData}
            onCloseModal={() => {
              handleCloseModal("hold");
              fetchApprovalData?.();
            }}
          />
        </Modal>
        <Modal isOpen={modalStates.stockistAck} isHeader={false} onCloseModal={() => handleCloseModal("stockistAck")} title="Accept Stockist Ack">
          <div className="flex flex-col gap-y-4 w-[500px] mx-auto bg-white p-4 border border-gray-300 shadow-lg rounded-lg">
            <p className="text-normal py-1 font-semibold text-[#1B1A1F]">Accept Stockist Ack</p>
            <p className="text-sm font-normal text-[#666666]">
              Accept the stockist acknowledgement for invoice {invoiceData?.invoice?.invoice_number}? Once accepted, this cannot be changed and the
              invoice becomes eligible for PO generation.
            </p>
            <div className="w-full">
              <p className="text-[#737274] font-medium text-xs ml-3 mb-1">
                Remarks <span className="text-red-500">*</span>
              </p>
              <textarea
                value={stockistAckRemarks}
                onChange={(event) => setStockistAckRemarks(event.target.value)}
                maxLength={500}
                rows={3}
                placeholder="Enter remarks"
                disabled={isAcceptingAck}
                className="outline-none text-sm px-3 py-1 border border-[#e0e0e0] rounded-lg w-full"
              />
              <p className="text-xs text-[#737274] text-right">{stockistAckRemarks.length}/500</p>
            </div>
            <div className="flex justify-end gap-x-2">
              <Button className="bg-transparent text-[#1B1A1F] border border-gray-300" disabled={isAcceptingAck} onClick={() => handleCloseModal("stockistAck")}>
                Cancel
              </Button>
              <Button
                className="text-white bg-[#8051FF]"
                disabled={isAcceptingAck || !stockistAckRemarks.trim()}
                isLoading={isAcceptingAck}
                onClick={handleConfirmStockistAck}
              >
                Confirm
              </Button>
            </div>
          </div>
        </Modal>
      </div>

      <header className={"flex justify-between w-full px-5 py-3"}>
        <div className="flex items-center gap-x-2">
          {isExpanded && <div className="w-1.5 h-1.5 bg-red-500 rounded-full mr-4"></div>}
          {invoiceData?.fileStatusHdr?.fuid_status_id === 1 && (
            <Checkbox
              checked={isSelected}
              onChange={() =>
                dispatch(
                  invoiceActions.toggleSelectedInvoice({
                    key: invoiceKey,
                    fuid_no: invoiceData?.fuid_no,
                    invoice_assignment_id: invoiceData?.InvoiceAssignment?.invoice_assignment_id,
                  })
                )
              }
              className="mt-4 ml-4"
            />
          )}
          {invoiceData?.is_prioritised && <img src={star} className="p-1 mr-2 w-8 h-8 rounded-lg border border-[#d0d0d0] shadow-md" alt="star" />}
          {!invoiceData?.is_prioritised && (
            <img src={starEmpty} className="p-1 mr-2 w-8 h-8 rounded-lg border border-[#d0d0d0] shadow-md" alt="star" />
          )}
          {[6, 8, 15, 24].includes(invoiceData?.fileStatusHdr?.fuid_status_id)? ( //M10-10902
            <h1 className={"font-semibold text-[1.2rem] mr-4"}>
              <span className="text-gray-400 text-[1.2rem]">Invoice#</span> {invoiceData?.invoice?.invoice_number}
            </h1>
          ) : (
            <h1 className={"font-semibold text-[1.2rem] mr-4"}>
              <span className="text-gray-400 text-[1.2rem]">FUID#</span> {invoiceData?.fuid_no}
            </h1>
          )}
          <h1
            className={`font-semibold  flex items-center text-xs h-6 px-2 rounded-full`}
            style={{ backgroundColor: invoiceData?.fileStatusHdr?.color_code?.trim(), color: invoiceData?.fileStatusHdr?.text_color_code?.trim() }}
          >
            {status}
            <img className="w-3 h-3 ml-1" src={downArrow} alt="down-arrow" />
          </h1>
          <p className="ml-4 text-[#8051FF] text-sm font-semibold border border-gray-400 rounded-full px-2">{invoiceData?.company_name}</p>
          {invoiceData?.po_stockist_ack_status === "PENDING" && (
            <p className="ml-4 text-[#92400E] text-sm font-semibold border bg-[#FEF3C7] rounded-full px-2 py-0.5">Stockist Ack: Pending</p>
          )}
          {invoiceData?.po_stockist_ack_status === "ACCEPTED" && (
            <p className="ml-4 text-[#166534] text-sm font-semibold border bg-[#DCFCE7] rounded-full px-2 py-0.5">Stockist Ack: Accepted</p>
          )}
          {invoiceData?.is_escalated && isEsalated && (
            <p className="ml-4 text-[#8E1F0B] text-sm font-semibold border bg-[#FEE9E8] rounded-full px-2 py-0.5">Escalated</p>
          )}
          {/* REJECTION-REVIEW-WORKFLOW: visible badge while a rejection review is pending */}
          {isRejectionPending && (
            <p className="ml-4 text-[#92400E] text-sm font-semibold border bg-[#FEF3C7] rounded-full px-2 py-0.5">
              Rejection Pending
            </p>
          )}
          {/* REJECTION-REVIEW-WORKFLOW: review completed — assignment exists but no longer pending */}
           {!isRejectionPending && invoiceData?.rejectionReviewAssignment?.invoice_assignment_id && (
            <span
              title="Rejection Reviewed"
              className="ml-4 w-3 h-3 rounded-full bg-[#f50505] inline-block"
            />
          )}
           {/* textract filter */}
          {invoiceData?.invoice?.textract &&(
            <p className="ml-4 text-[#055160] text-sm font-semibold border bg-[#E0F2FE] rounded-full px-2 py-0.5">MV Lens</p> //change name
          )}
          {/* M10-16200: category 1 e-invoice status */}
          {[2, 8].includes(invoiceData?.fileStatusHdr?.fuid_status_id) && invoiceData?.cat1_einvoice_status && (
            <p className={`ml-4 text-sm font-semibold border rounded-full px-2 py-0.5 ${cat1EinvoiceStatusColor[invoiceData.cat1_einvoice_status?.toLowerCase()] || "text-[#055160] bg-[#E0F2FE]"}`}>
              Cat-1 Verification : <span className="capitalize">{invoiceData.cat1_einvoice_status}</span>
            </p>
          )}

          {invoiceData?.is_prioritised && latestRemarks(invoiceData?.invoice?.invoiceRemarks, invoiceData?.fileStatusHdr?.fuid_status_id, invoiceData?.invoice?.company_verification_status) && (
            <p className="ml-4 text-[#055160] text-sm font-semibold border bg-[#E0F2FE] rounded-full px-2 py-0.5">{latestRemarks(invoiceData?.invoice?.invoiceRemarks, invoiceData?.fileStatusHdr?.fuid_status_id, invoiceData?.invoice?.company_verification_status)}</p>
          )}

        </div>

        <div className="flex items-center gap-x-2">
          {viewDetails && !detailsPermissionLoading && hasViewDetailsPermission && (
            <Button
              className="h-10 text-white bg-[#8051FF] shadow-sm"
              onClick={() => {
                if (invoiceData?.fileStatusHdr?.fuid_status_id === 1) {
                  toast({
                    description: "Yet To Processed can be viewed in the entry screen Only",
                    variant: "destructive",
                  });
                  return;
                }
                //M10-11537 && M10-12587
                if (invoiceData?.fileStatusHdr?.fuid_status_id === 22 || invoiceData?.fileStatusHdr?.fuid_status_id === 23) {
                  toast({
                    description: `${invoiceData?.fileStatusHdr?.fuid_status_shortcode} can be viewed in the entry screen Only`,
                    variant: "destructive",
                  });
                  return;
                }
                //M10-11537 && M10-12587
                if (invoiceData?.fileStatusHdr?.fuid_status_id === 9) {
                  toast({
                    description: "This invoice has been handled in v1",
                    variant: "destructive",
                  });
                  return;
                }
                if (invoiceData?.fileStatusHdr?.fuid_status_id === 2) {
                  toast({
                    description: "This invoice cannot be opened",
                    variant: "destructive",
                  });
                  return;
                }
                // REJECTION-REVIEW-WORKFLOW: original user is blocked from entering
                // while a rejection review is pending. The assigned reviewer accesses
                // the same invoice via the FUID Rejection tab (isFuidRejectionReview).
                if (isRejectionPending && !isFuidRejectionReview) {
                  toast({
                    description: "Invoice Rejected — awaiting reviewer action. Entry is blocked.",
                    variant: "destructive",
                  });
                  return;
                }
                if (approvalScreen) {
                  //M10-9862
                  naviagate(`/transactions/invoice/${invoiceData?.stockist_invoice_attachment_id}/${1}`, {
                    state: {
                      CompanyCode: invoiceData?.company_code,
                      fuidRejectionReviewMode: isFuidRejectionReview,
                    },
                  });
                } else {
                  naviagate(`/transactions/invoice/${invoiceData?.stockist_invoice_attachment_id}/${0}`, {
                    state: {
                      CompanyCode: invoiceData?.company_code,
                      fuidRejectionReviewMode: isFuidRejectionReview,
                    },
                  });
                }
              }}
            >
              View Details
            </Button>
          )}
          {
            <div className="w-10 h-10 text-white shadow-sm">
              <InvoiceDropdown
                placeholder="..."
                menuItems={filteredMenuItems}
                displayKey="label"
                onOptionClick={handleOptionClick}
                currentSelected={undefined}
                setModalStates={setModalStates}
              />
            </div>
          }
          {isExpandable && (
            <Button className="w-10 h-10 bg-[#F1F1F1] border border-1-black" onClick={handleToggle}>
              <img className={`transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`} src={downArrow} alt="down-arrow" />
            </Button>
          )}
        </div>
      </header>
      <div className="flex items-center gap-x-2 ml-4">
        <img className="w-6 h-6 ml-1" src={Contact} alt="person-img" />
        <span className="text-[#49484C] text-sm">Assigned to</span>
        <div className="w-8 h-8 bg-[#8051FF] flex items-center justify-center text-[#fff] rounded-full">
          {invoiceData?.InvoiceAssignment?.user_name?.slice(0, 2)}
        </div>
        <span className="text-[#49484C] text-sm">{invoiceData?.InvoiceAssignment?.user_name}</span>
        {invoiceData?.is_rejection_pending && invoiceData?.rejectionReviewAssignment?.user_name &&
          <>
            <img className="w-6 h-6 ml-10" src={Contact} alt="person-img" />
            <span className="text-[#49484C] text-sm">Escalated to</span>
            <div className="w-10 h-10 bg-[#8051FF] flex items-center justify-center text-[#fff] rounded-full">
              {invoiceData?.rejectionReviewAssignment?.user_name?.slice(0, 2)}
            </div>
            <span className="text-[#49484C] text-sm">{invoiceData?.rejectionReviewAssignment?.user_name}</span>
          </>}
      </div>
      <div
        className={`overflow-hidden transition-all duration-500 ease-in-out ${isExpanded ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"}`}
        style={{ transitionProperty: "max-height, opacity" }}
      >
        <div className={"grid grid-cols-1 w-[80%] gap-x-10 px-5 py-3 mt-5 xl:grid-cols-2 gap-y-5"}>
          {!!data &&
            Object.keys(data)?.length > 0 &&
            Object.entries(data)?.map(
              ([key, value]) =>
                typeof value === "string" &&
                value !== "" && (
                  <div key={key?.toString()} className={"grid grid-cols-2 -space-x-10 -space-y-1 text-sm xl:grid-cols-2"}>
                    <p className="text-[#49484C] place-self-center mr-auto whitespace-break-spaces text-sm leading-[0.02rem] font-normal not-italic">
                      {switchString(key)} :
                    </p>
                    <div className="mr-auto">
                      {key === "Counter Code" ? (
                        <a
                          className="text-gray-900 hover:text-[#6b40d6]"
                          href={`/masters/counters/details/?counter_code=${value}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => {
                            dispatch(counterActions.setClickType("row"));
                            dispatch(counterActions.setSelectedCounterData({}));
                            window.localStorage.setItem("counterOrderId", value);
                            window.localStorage.setItem("counterCompanyLocationId", invoiceData?.counter_company_location_id);
                          }}
                        >
                          {value}
                        </a>
                      ) : key === "Order Number" && value !== "-" ? (
                        value?.split(",").map((orderCode: string, index: number) => {
                          const trimmedCode = orderCode.trim();
                          return (
                            <span key={index}>
                              <a
                                className="text-gray-900 hover:text-[#6b40d6] w-fit"
                                href={`/transactions/orders/info/${trimmedCode}/1`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => {
                                  window.localStorage.setItem("counterCompanyLocationId", invoiceData?.counter_company_location_id);
                                  window.localStorage.setItem("company_code", invoiceData?.company_code);
                                }}
                              >
                                {trimmedCode}
                              </a>
                              {index < value.split(",").length - 1 && ", "}
                            </span>
                          );
                        })
                      ) : checkDataType ? (
                        <p className="text-[#1B1A1F] mr-auto">
                          {typeof value === "string" ? verifyStringType(value) : ""}
                        </p>
                      ) : (
                        <p className="text-[#1B1A1F] mr-auto">{value}</p>
                      )}
                    </div>
                  </div>
                )
            )}
        </div>
        {shouldShowRejectionDetails(invoiceData) && <RejectionDetailsCard invoiceData={invoiceData} />}
      </div>
    </div>
  );
};

export default InvoiceApprovalDetailCard;
